import React, { useState, useRef, useMemo } from 'react';
import {
  useFloating,
  arrow,
  autoUpdate,
  useInteractions,
  useDismiss,
  UseFloatingReturn,
  useRole,
  FloatingContext,
  hide,
  offset,
} from '@floating-ui/react';

export interface useFreeTrialNudgeReturn {
  isFreeTrialNudgeOpen: boolean;
  setFreeTrialNudgeOpenState: React.Dispatch<React.SetStateAction<boolean>>;
  middleWareData: UseFloatingReturn['middlewareData'];
  triggerProps: {
    ref: UseFloatingReturn['refs']['setReference'];
  };
  nudgeProps: {
    context: FloatingContext;
    arrowref: React.RefObject<SVGSVGElement>;
    ref: UseFloatingReturn['refs']['setFloating'];
    style: UseFloatingReturn['floatingStyles'];
  };
}

/**
 * Custom hook for managing free trial nudge behavior using @floating-ui/react
 */
export const useFreeTrialNudge = () => {
  //This is used to control the open state of the free trial nudge for display and dismissal
  const [isFreeTrialNudgeOpen, setFreeTrialNudgeOpenState] = useState(true);

  const arrowref = useRef<SVGSVGElement | null>(null);

  const { refs, floatingStyles, middlewareData, context } = useFloating({
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    open: isFreeTrialNudgeOpen,
    onOpenChange: setFreeTrialNudgeOpenState,
    placement: 'left-start',
    middleware: [
      offset({ mainAxis: 25, crossAxis: -16 }),
      arrow({
        element: arrowref,
        padding: 18,
      }),
      hide(),
    ],
  });

  const role = useRole(context);
  const dismiss = useDismiss(context, {
    escapeKey: false,
    outsidePress: false,
    referencePress: true,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, role]);

  return useMemo(
    () => ({
      isFreeTrialNudgeOpen,
      setFreeTrialNudgeOpenState,
      middlewareData,
      triggerProps: {
        ...(getReferenceProps({ ref: refs.setReference }) as { ref: UseFloatingReturn['refs']['setReference'] }),
      },
      nudgeProps: {
        ...(getFloatingProps({
          ref: refs.setFloating,
          style: floatingStyles,
        }) as {
          ref: UseFloatingReturn['refs']['setFloating'];
          style: UseFloatingReturn['floatingStyles'];
        }),
        arrowref,
        context,
      },
    }),
    [refs, floatingStyles, middlewareData, getFloatingProps, getReferenceProps]
  );
};
